import React, { useMemo } from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import { calculateAssetMonthlyImpact, resolveActiveAssetEvents } from "../../utils/chart/burndown.utils";
import { calculateExpenseAtMonth } from "../../utils/chart/scaling.utils";
import {
  calculateMonthlyCustomerMetrics,
  type CustomerProjectionData,
} from "../../utils/chart/customer-projection.utils";
import { safeDate } from "../../utils/date";
import useResponsive from "../../hooks/useResponsive";
import { CHART_COLORS } from "@/constants/chart-colors";
import {
  isAcquisitionRelatedExpense,
  usesProjectedCustomerAcquisitionCosts,
} from "../../utils/chart/acquisition-expense.utils";
import type { Expense, TimelineEvent } from "../../types/startup.types";

interface ExpenseBreakdownProps {
  expenses: Expense[];
  events?: TimelineEvent[];
  customerProjectionData?: CustomerProjectionData;
  currentDate?: Date;
}

const CATEGORY_COLORS: Record<string, string> = {
  salaries: "#7c9fd4",
  marketing: "#9b8ec4",
  operations: "#a78bfa",
  technology: "#d4b87a",
  legal: CHART_COLORS.expense,
  rent: "#7ab8c9",
  travel: "#b5aad8",
  equipment: "#d4a0ad",
  consulting: "#c4a5b8",
  "asset depreciation": CHART_COLORS.expenseMuted,
  "customer acquisition": CHART_COLORS.expense,
  other: "#8f879e",
};

const ExpenseBreakdownChart: React.FC<ExpenseBreakdownProps> = ({
  expenses,
  events = [],
  customerProjectionData,
  currentDate = new Date(), // Default to current date if not provided
}) => {
  const { isMobile, isTablet } = useResponsive();

  // Responsive chart sizing
  const chartConfig = useMemo(() => {
    if (isMobile) {
      return {
        cx: "50%",
        cy: "45%",
        innerRadius: 20,
        outerRadius: 50,
        legendHeight: 60,
        showLegend: false,
      };
    } else if (isTablet) {
      return {
        cx: "50%",
        cy: "42%",
        innerRadius: 22,
        outerRadius: 55,
        legendHeight: 50,
        showLegend: true,
      };
    } else {
      return {
        cx: "50%",
        cy: "40%",
        innerRadius: 25,
        outerRadius: 60,
        legendHeight: 40,
        showLegend: true,
      };
    }
  }, [isMobile, isTablet]);

  // Calculate total expenses by category for the specific date
  const chartData = useMemo(() => {
    const categoryTotals: Record<string, number> = {};
    const currentMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      1
    );

    // Calculate customer metrics if available for customer-based expenses
    let customerMetrics: any = undefined;
    if (customerProjectionData) {
      customerMetrics = calculateMonthlyCustomerMetrics(
        currentMonth,
        customerProjectionData
      );
    }

    // Process regular expenses - only those active at the current date
    expenses.forEach((expense) => {
      // Skip expenses without start dates
      if (!expense.startDate) return;

      if (
        usesProjectedCustomerAcquisitionCosts(customerProjectionData) &&
        isAcquisitionRelatedExpense(expense) &&
        expense.id !== "customer-acquisition-costs"
      ) {
        return;
      }

      // Check if expense is active at the current date
      const expenseStartDate = safeDate(expense.startDate);
      const expenseStart = new Date(
        expenseStartDate.getFullYear(),
        expenseStartDate.getMonth(),
        1
      );
      const expenseEnd = expense.endDate
        ? new Date(
            safeDate(expense.endDate).getFullYear(),
            safeDate(expense.endDate).getMonth(),
            1
          )
        : new Date(2099, 11, 31); // Far future if no end date

      // Only include if expense is active at current date
      if (currentMonth >= expenseStart && currentMonth <= expenseEnd) {
        let monthlyAmount = 0;

        // Handle one-time expenses
        if (expense.frequency === "one-time") {
          // One-time expenses only show if they occurred in current month or before
          const expenseMonth = new Date(
            expenseStartDate.getFullYear(),
            expenseStartDate.getMonth(),
            1
          );
          if (expenseMonth.getTime() === currentMonth.getTime()) {
            monthlyAmount = expense.oneTimeAmount || expense.amount || 0;
          }
        } else {
          // For recurring expenses, calculate actual amount for this month using scaling utilities
          const expenseForCalculation = {
            ...expense,
            // For customer-based expenses, don't pass the stored amount as it should be calculated dynamically
            amount:
              expense.expenseType === "customer_based"
                ? 0
                : expense.monthlyAmount || expense.amount || 0,
            frequency: "monthly" as const,
            expenseType: expense.expenseType || "fixed",
            growthRate: expense.growthRate,
            revenueRatio: expense.revenueRatio,
            customerBaseCostPerCustomer: expense.customerBaseCostPerCustomer,
            customerBaseAmount: expense.customerBaseAmount,
            isRecurring: true,
            startupId: "",
            createdAt: new Date(),
            updatedAt: new Date(),
          };

          monthlyAmount = calculateExpenseAtMonth(
            expenseForCalculation,
            currentMonth,
            0,
            customerMetrics
          ); // Pass customer data for customer-based expenses
        }

        if (monthlyAmount > 0) {
          const category = expense.category.toLowerCase();
          categoryTotals[category] =
            (categoryTotals[category] || 0) + monthlyAmount;
        }
      }
    });

    const assetEvents = resolveActiveAssetEvents(
      (events ?? []).map((event) => ({
        id: event.id,
        date: safeDate(event.date),
        type: "asset" as const,
        priority: event.priority,
        description: event.description || event.title || "",
        amount: event.amount,
        monthlyAppreciationRate: event.monthlyAppreciationRate,
        minimumValue: event.minimumValue,
        endDate: event.endDate ? safeDate(event.endDate) : undefined,
        lineItemGroupId: event.lineItemGroupId,
        segmentKind: event.segmentKind,
      })),
      currentMonth,
    );

    const totalAssetDepreciation = assetEvents.reduce((total, event) => {
      const monthlyImpact = calculateAssetMonthlyImpact(event, currentMonth);
      return total + Math.abs(Math.min(0, monthlyImpact));
    }, 0);

    if (totalAssetDepreciation > 0) {
      categoryTotals["asset depreciation"] = totalAssetDepreciation;
    }

    // Add projected CAC only when not already represented in the expense list
    if (customerProjectionData) {
      const hasAcquisitionLine = expenses.some(isAcquisitionRelatedExpense);
      if (!hasAcquisitionLine) {
        const customerMetrics = calculateMonthlyCustomerMetrics(
          currentMonth,
          customerProjectionData,
        );
        if (customerMetrics.acquisitionCosts > 0) {
          categoryTotals["customer acquisition"] =
            customerMetrics.acquisitionCosts;
        }
      }
    }

    return Object.entries(categoryTotals)
      .map(([category, amount]) => ({
        name: category.charAt(0).toUpperCase() + category.slice(1),
        value: amount,
        color: CATEGORY_COLORS[category] || "#6b7280",
      }))
      .sort((a, b) => b.value - a.value);
  }, [expenses, events, customerProjectionData, currentDate]);

  const totalExpenses = chartData.reduce((sum, item) => sum + item.value, 0);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const percentage = ((data.value / totalExpenses) * 100).toFixed(1);

      return (
        <div className="bg-surface-elevated text-white text-xs px-3 py-2 rounded-lg shadow-glow">
          <p className="font-semibold">{data.payload.name}</p>
          <p className="text-expense">${data.value.toLocaleString()}/month</p>
          <p className="text-white/55">{percentage}% of total</p>
        </div>
      );
    }
    return null;
  };

  if (chartData.length === 0) {
    return (
      <div className="bg-surface-elevated rounded-lg border h-full flex flex-col">
        <div className="px-4 py-3 border-b bg-white/[0.06] flex-shrink-0">
          <h4 className="text-sm font-medium text-white/75">
            Expense Breakdown
          </h4>
          <p className="text-xs text-white/45 mt-1">No expense data</p>
        </div>
        <div className="p-4 flex-1 flex items-center justify-center">
          <p className="text-white/45 text-sm">No expense data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface-elevated rounded-lg border h-full flex flex-col">
      <div className="px-4 py-3 border-b bg-white/[0.06] flex-shrink-0">
        <h4 className="text-sm font-medium text-white/75">Expense Breakdown</h4>
        <p className="text-xs text-white/45 mt-1">
          {currentDate.toLocaleDateString("en-US", {
            month: "short",
            year: "numeric",
          })}
          : ${totalExpenses.toLocaleString()}/month
        </p>
      </div>

      <div className="p-4 flex-1 min-h-0 pb-6">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx={chartConfig.cx}
              cy={chartConfig.cy}
              innerRadius={chartConfig.innerRadius}
              outerRadius={chartConfig.outerRadius}
              paddingAngle={2}
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            {chartConfig.showLegend && (
              <Legend
                verticalAlign="bottom"
                height={chartConfig.legendHeight}
                formatter={(value, entry) => {
                  if (isMobile) {
                    return value.split(" ")[0];
                  }
                  return `${value} ($${(
                    entry.payload?.value || 0
                  ).toLocaleString()})`;
                }}
                wrapperStyle={{
                  fontSize: isMobile ? "9px" : "10px",
                  lineHeight: "1.2",
                  paddingTop: "4px",
                  paddingBottom: "8px",
                }}
              />
            )}
          </PieChart>
        </ResponsiveContainer>

        {/* Mobile Legend Alternative */}
        {isMobile && chartData.length > 0 && (
          <div className="mt-2 space-y-1">
            {chartData.map((entry, index) => (
              <div
                key={index}
                className="flex items-center justify-between text-xs"
              >
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="text-white/75">{entry.name}</span>
                </div>
                <span className="text-white/65 font-medium">
                  ${entry.value.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpenseBreakdownChart;
